import Joyride, {
  Step,
  CallBackProps,
  STATUS,
  TooltipRenderProps,
} from "react-joyride";
import { colors } from "constants/colors";
import { Button } from "../Button/Button";

const TOOLTIP_BG_COLOR = colors.neutral[100];
const SPOTLIGHT_BORDER_COLOR = colors.purple[10];

type ProductTourProps = {
  steps: Step[];
  run: boolean;
  onComplete: () => void;
  onSkip?: () => void;
  onStart?: () => void;
};

const TourStepTooltip = ({
  step,
  tooltipProps,
  isLastStep,
  primaryProps,
  skipProps,
  onComplete,
  onSkip,
}: TooltipRenderProps & {
  onComplete: () => void;
  onSkip?: () => void;
}) => {
  const handlePrimaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isLastStep) {
      onComplete();
    }
    primaryProps.onClick(e);
  };

  const handleSkipClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onSkip?.();
    skipProps.onClick(e);
  };

  const primaryButtonText = isLastStep ? "Done" : "Next";

  return (
    <div {...tooltipProps} className="t-relative t-pointer-events-auto">
      <div className="t-rounded-xl t-p-4 t-max-w-[320px] t-relative t-pointer-events-auto t-bg-neutral-100 t-shadow-lg">
        {step.title && (
          <div className="t-text-body-sm t-italic t-mb-2 t-text-left t-text-neutral-40">
            {step.title}
          </div>
        )}
        <div className="t-text-subtext-sm t-mb-4 t-text-left t-text-white">
          {step.content}
        </div>
        <div className="t-flex t-justify-between t-items-center t-gap-2 t-relative t-z-10">
          <Button
            onClick={handlePrimaryClick}
            customType="secondary"
            size="small"
          >
            {primaryButtonText}
          </Button>
          <Button
            customType="link_neutral"
            size="regular"
            onClick={handleSkipClick}
          >
            Skip
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ProductTour = ({
  steps,
  run,
  onComplete,
  onSkip,
  onStart,
}: ProductTourProps) => {
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (status === STATUS.RUNNING && action === "start") {
      onStart?.();
    }
  };

  if (!run || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      callback={handleJoyrideCallback}
      disableOverlay={false}
      spotlightClicks={false}
      spotlightPadding={4}
      disableScrolling={false}
      hideBackButton
      scrollToFirstStep
      scrollOffset={100}
      tooltipComponent={(props) => (
        <TourStepTooltip {...props} onComplete={onComplete} onSkip={onSkip} />
      )}
      floaterProps={{
        disableFlip: false,
      }}
      styles={{
        options: {
          zIndex: 99999,
          arrowColor: TOOLTIP_BG_COLOR,
          overlayColor: "rgba(0, 0, 0, 0.3)",
          spotlightShadow: "0 0 0 9999px rgba(0, 0, 0, 0.3)",
        },
        spotlight: {
          borderRadius: "12px",
          border: `2px solid ${SPOTLIGHT_BORDER_COLOR}`,
        },
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        },
      }}
    />
  );
};
